import { useEffect, useState } from "react";
import type { DateTimeRangeValue } from "@/shared/stores/useDateTimeRangeStore.ts";

/** Minimum span of the realtime fetch window (the fetched "last N minutes"). */
export const REALTIME_SPAN_MS = 30 * 60 * 1000;

/**
 * Default visible span of the realtime *view*: the timeline auto-zooms to the
 * trailing N minutes of the fetched window and scrolls continuously.
 */
export const REALTIME_VIEW_SPAN_MS = 10 * 60 * 1000;

/**
 * Where the live cursor sits across the view: 0.9 → 90% of the width, leaving
 * the last 10% for the newest events, which run ahead of the video cursor by the
 * footage-upload lag.
 */
export const LIVE_CURSOR_ANCHOR = 0.9;

/**
 * The slack reserved to the RIGHT of the live cursor — and therefore the largest
 * cursor lag the anchored view absorbs before it starts following the wall clock
 * instead (see `useLiveTimelineDomain`).
 *
 * Rounded to whole milliseconds: `600000 * (1 - 0.9)` is 59999.999…, which would
 * make a cursor sitting at exactly one minute of lag test as PAST the threshold
 * and trigger a re-sync on ordinary footage lag.
 */
export const liveLeadMs = (spanMs: number, anchor: number = LIVE_CURSOR_ANCHOR): number =>
  Math.round(spanMs * (1 - anchor));

/**
 * How far the playback position may trail the newest available footage and
 * still count as LIVE.
 *
 * Clips run ~30s, so being inside one is normal and is not worth reporting. This
 * matches the video tile's own catch-up threshold — the point at which it
 * considers itself meaningfully behind and jumps forward — so the badge and the
 * recovery agree about what "behind" means rather than each having a private
 * opinion.
 */
export const LIVE_LAG_TOLERANCE_MS = 90_000;

/**
 * How far behind the live edge the playback position is, in ms. Null when either
 * end is unknown (no footage loaded yet, cursor not seeded). Never negative: the
 * cursor legitimately sits a little ahead of the newest clip while waiting for
 * the next upload, and that is not "ahead of live", it IS live.
 */
export const liveLagMs = (
  footageEndMs: number | null,
  cursorMs: number | null,
): number | null => {
  if (footageEndMs == null || cursorMs == null) return null;
  return Math.max(0, footageEndMs - cursorMs);
};

/**
 * Whether the view may honestly call itself live.
 *
 * Being armed and having a playing `<video>` element is NOT enough, which is
 * what the badge used to check: a tile happily playing footage from five minutes
 * ago satisfies both while showing the operator a pulsing "Live". A live claim
 * has to be about WHERE the playback position is, not merely that pixels are
 * moving — otherwise the one indicator that tells an operator whether they are
 * watching now is the one thing they cannot trust.
 */
export const isTrulyLive = (
  followArmed: boolean,
  isPlaying: boolean,
  lagMs: number | null,
): boolean => {
  if (!followArmed || !isPlaying) return false;
  // Unknown lag (no footage yet) falls back to the armed+playing reading — the
  // tile is doing what it can, and there is nothing to contradict it with.
  if (lagMs == null) return true;
  return lagMs <= LIVE_LAG_TOLERANCE_MS;
};

/**
 * Whether returning to the tab should force a full live re-sync.
 *
 * A backgrounded tab pauses `requestAnimationFrame`, so the video's playback
 * cursor — which IS the live cursor — stops dead while the wall clock does not.
 * Come back after five minutes and the cursor is five minutes stale: the view
 * keeps following the clock (the domain floors its edge at `now - lead`) while
 * the cursor slides left, landing at 50% of a 10-minute view instead of its
 * usual 90%. That is not a rendering artefact — the video really is that far
 * behind.
 *
 * Re-syncing unconditionally is the obvious fix and the wrong one: it re-fetches
 * footage on EVERY tab switch, which is the "everything reloads when I come back"
 * complaint. So gate it on the lag actually mattering — precisely when the cursor
 * has fallen further behind than the view's own lead, i.e. when it can no longer
 * hold its anchored position. A quick glance at another tab stays free.
 *
 * Pure so the rule is testable: real tab-backgrounding cannot be emulated
 * (`document.hidden` is read-only and synthetic events do not pause rAF).
 */
export const shouldResyncOnReturn = (
  cursorMs: number | null,
  nowMs: number,
  leadMs: number,
): boolean => {
  if (cursorMs == null) return false;
  return nowMs - cursorMs > leadMs;
};

/** "10m" / "1h" — human label for a live view span. */
export const formatLiveSpanLabel = (spanMs: number): string =>
  spanMs >= 60 * 60 * 1000 ? `${spanMs / (60 * 60 * 1000)}h` : `${spanMs / (60 * 1000)}m`;

/** User-selectable live view spans for the map timeline's realtime mode. */
export const LIVE_SPAN_OPTIONS = (
  [5 * 60 * 1000, 10 * 60 * 1000, 30 * 60 * 1000, 60 * 60 * 1000] as const
).map((ms) => ({ ms, label: formatLiveSpanLabel(ms) }));

/**
 * Whether reviewing `alertMs` can stay in realtime, i.e. whether the review view
 * fits inside the rolling fetch window.
 *
 * The review view is CENTERED on the moment (`reviewTime ± spanMs/2`, see
 * `timeline-editor`'s `reviewDomain`), so "the alert itself is inside the
 * window" is not the question — its LEFT HALF has to be inside too. Asking only
 * about the instant let an alert 58 minutes old pass with a ~65-minute window
 * and a 1h span: the view then spanned 88 to 28 minutes ago, and everything
 * before ~65 minutes ago had never been fetched by any query. It rendered
 * event-free and video-free, identical to a genuinely idle machine — and since
 * every row shares the window, it read as "the whole site was quiet".
 */
export const alertFitsLiveWindow = (
  alertMs: number | null,
  windowFromMs: number,
  spanMs: number,
): boolean => alertMs != null && alertMs - spanMs / 2 >= windowFromMs;

/**
 * The window advances in 5-minute steps: the end edge is quantized UP to the
 * next 5-minute boundary (slightly in the future), so the React Query key only
 * changes every 5 minutes while the 5s poll keeps filling in new events.
 */
const BUCKET_MS = 5 * 60 * 1000;
const CHECK_MS = 15 * 1000;

const computeWindow = (spanMs: number): DateTimeRangeValue => {
  const end = Math.ceil(Date.now() / BUCKET_MS) * BUCKET_MS;
  return {
    // One extra bucket of lead-in so the view always covers at least the full
    // span (at most span + 5 minutes, right after a bucket flip).
    fromDate: new Date(end - BUCKET_MS - spanMs),
    toDate: new Date(end),
  };
};

/**
 * Rolling fetch window for the map timeline's realtime mode. Always covers at
 * least `REALTIME_SPAN_MS`, widened to `viewSpanMs` when the user picks a
 * larger live view span.
 */
export const useRealtimeWindow = (
  enabled: boolean,
  viewSpanMs: number = REALTIME_VIEW_SPAN_MS,
  /**
   * Stop advancing and hold the current window.
   *
   * Reviewing an alert stays in realtime — only the DOMAIN freezes, centred on
   * the reviewed moment. The fetch window kept rolling underneath it, so after a
   * few 5-minute bucket flips the reviewed moment fell out of the range being
   * fetched, the new response replaced the old one, and the segments under
   * examination vanished from a view that was still frozen and still looked
   * authoritative. Freezing the window too keeps what is on screen fetched for
   * as long as it is being looked at.
   */
  frozen: boolean = false,
): DateTimeRangeValue => {
  const spanMs = Math.max(REALTIME_SPAN_MS, viewSpanMs);
  const [win, setWin] = useState<DateTimeRangeValue>(() => computeWindow(spanMs));

  useEffect(() => {
    // Unfreezing re-runs this and `advance()` re-quantizes immediately, so the
    // window catches up the moment review ends.
    if (!enabled || frozen) return;
    const advance = () =>
      setWin((prev) => {
        const next = computeWindow(spanMs);
        // Keep the same object (and thus the same query keys) until the
        // 5-minute bucket flips or the span changes.
        return next.toDate.getTime() === prev.toDate.getTime() &&
          next.fromDate.getTime() === prev.fromDate.getTime()
          ? prev
          : next;
      });
    advance();
    const id = setInterval(advance, CHECK_MS);
    // Browsers throttle a hidden tab's timers to ~1/minute, so on return the
    // window can be a bucket behind — the live view would roll forward over a
    // stretch nothing has fetched yet (an empty gap at its right edge) until the
    // next tick. Re-quantize the moment the tab is visible again.
    const onVisibility = () => {
      if (!document.hidden) advance();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [enabled, spanMs, frozen]);

  return win;
};
